import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '../store'
import { DEFAULT_LOCALE, isSupportedLocale, normalizeLocale } from './locale'
import { ROUTE_SEO } from '../utils/seo'

Vue.use(VueRouter)

const pageRoutes = [
  {
    path: '/',
    name: 'Index',
    meta: { seo: ROUTE_SEO.Index },
    component: () => import('../pages/index/index.vue')
  },
  {
    path: '/about',
    name: 'About',
    meta: { seo: ROUTE_SEO.About },
    component: () => import('../pages/about/index.vue')
  },
  {
    path: '/services',
    name: 'Services',
    meta: { seo: ROUTE_SEO.Services },
    component: () => import('../pages/services/index.vue')
  },
  {
    path: '/services/aircraft-asset-management',
    name: 'AircraftAssetManagement',
    meta: { seo: ROUTE_SEO.AircraftAssetManagement },
    component: () => import('../pages/services/aircraft-asset-management.vue')
  },
  {
    path: '/services/charter-operations',
    name: 'CharterOperations',
    meta: { seo: ROUTE_SEO.CharterOperations },
    component: () => import('../pages/services/charter-operations.vue')
  },
  {
    path: '/services/flight-operations-support',
    name: 'FlightOperationsSupport',
    meta: { seo: ROUTE_SEO.FlightOperationsSupport },
    component: () => import('../pages/services/flight-operations-support.vue')
  },
  {
    path: '/services/aviation-material-trading',
    name: 'AviationMaterialTrading',
    meta: { seo: ROUTE_SEO.AviationMaterialTrading },
    component: () => import('../pages/services/aviation-material-trading.vue')
  },
  {
    path: '/fleet',
    name: 'Fleet',
    meta: { seo: ROUTE_SEO.Fleet },
    component: () => import('../pages/fleet/index.vue')
  },
  {
    path: '/fleet/B747-400BDSF',
    name: 'B747-400BDSF',
    meta: { seo: ROUTE_SEO['B747-400BDSF'] },
    component: () => import('../pages/fleet/B747-400BDSF.vue')
  },
  {
    path: '/fleet/B747-400F',
    name: 'B747-400F',
    meta: { seo: ROUTE_SEO['B747-400F'] },
    component: () => import('../pages/fleet/B747-400F.vue')
  },
  {
    path: '/fleet/B777-300ERSF',
    name: 'B777-300ERSF',
    meta: { seo: ROUTE_SEO['B777-300ERSF'] },
    component: () => import('../pages/fleet/B777-300ERSF.vue')
  },
  {
    path: '/article/:id',
    name: 'Article',
    meta: { seo: ROUTE_SEO.Article },
    component: () => import('../pages/article/index.vue')
  },
  {
    path: '/news',
    name: 'News',
    meta: { seo: ROUTE_SEO.News },
    component: () => import('../pages/news/index.vue')
  },
  {
    path: '/careers',
    name: 'Careers',
    meta: { seo: ROUTE_SEO.Careers },
    component: () => import('../pages/careers/index.vue')
  },
  {
    path: '/investors',
    name: 'Investors',
    meta: { seo: ROUTE_SEO.Investors },
    component: () => import('../pages/investors/index.vue')
  },
  {
    path: '/contact',
    name: 'Contact',
    meta: { seo: ROUTE_SEO.Contact },
    component: () => import('../pages/contact/index.vue')
  }
]

const withLocalePrefix = route => ({
  ...route,
  path: `/:locale${route.path === '/' ? '/' : route.path}`
})

const redirectToDefaultLocale = to => ({
  path: `/${DEFAULT_LOCALE}/`,
  query: to.query,
  hash: to.hash
})

const redirectToLocaleIndex = to => {
  const locale = normalizeLocale(to.params.locale)
  if (!isSupportedLocale(locale)) {
    return redirectToDefaultLocale(to)
  }
  return {
    path: `/${locale}/`,
    query: to.query,
    hash: to.hash
  }
}

const legacyRedirect = route => ({
  path: route.path,
  redirect: to => ({
    name: route.name,
    params: {
      ...to.params,
      locale: DEFAULT_LOCALE
    },
    query: to.query,
    hash: to.hash
  })
})

const legacyRedirects = pageRoutes
  .filter(route => route.path !== '/')
  .flatMap(route => [
    legacyRedirect(route),
    legacyRedirect({ ...route, path: `${route.path}/` })
  ])

const routes = [
  {
    path: '/',
    redirect: redirectToDefaultLocale
  },
  ...legacyRedirects,
  ...pageRoutes.map(withLocalePrefix),
  {
    path: '/:locale',
    redirect: redirectToLocaleIndex
  },
  {
    path: '/:locale/*',
    name: 'NotFound',
    meta: { seo: ROUTE_SEO.NotFound },
    component: () => import('../pages/not-found/index.vue')
  },
  {
    path: '*',
    redirect: to => ({
      name: 'NotFound',
      params: {
        locale: DEFAULT_LOCALE,
        pathMatch: to.path.replace(/^\/+/, '')
      },
      query: to.query,
      hash: to.hash
    })
  }
]

const router = new VueRouter({
  mode: 'history',
  // Asset delivery can use an absolute publicPath without changing SPA routes.
  base: '/',
  strict: true,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash,
        behavior: 'smooth'
      }
    }
    if (savedPosition) {
      return savedPosition
    }
    return { x: 0, y: 0 }
  }
})

router.beforeEach((to, from, next) => {
  const locale = normalizeLocale(to.params.locale)
  if (!locale) {
    next()
    return
  }
  if (!isSupportedLocale(locale)) {
    next({
      path: `/${DEFAULT_LOCALE}/`,
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }
  if (to.name === 'Index' && to.path !== `/${locale}/`) {
    next({
      path: `/${locale}/`,
      query: to.query,
      hash: to.hash,
      replace: true
    })
    return
  }
  store.commit('setLang', locale)
  next()
})

export default router
